import { useState, forwardRef } from "react";
import { useSnackbar } from 'notistack';
// mui
import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
  Stack,
  TextField,
  OutlinedInput,
  Alert,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { LoadingButton } from "@mui/lab";
// hooks
import useAuth from "../../../hooks/useAuth";
import useResponsive from '../../../hooks/useResponsive'
// components
import { IMaskInput } from 'react-imask';
import Iconify from '../../../components/Iconify';
// utils
import axios from '../../../utils/axios';


const InfoText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.info.main,
  };
});

const CVCMaskTextInput = forwardRef(
  function CVCMaskTextInput(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="0000"
        inputRef={ref}
        onAccept={(value) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  },
);

const NumberMaskTextInput = forwardRef(
  function CVCMaskTextInput(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="0000 0000 0000 0000"
        inputRef={ref}
        onAccept={(value) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  },
);

const PeriodMaskTextInput = forwardRef(
  function CVCMaskTextInput(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="00/00"
        inputRef={ref}
        onAccept={(value) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  },
);

// ------------------------------------------------------------------------------

export default function MeetingDepositPaymentDialog({ isOpen, closeDialog, jobPostId, candidateId, paid }) {
  const [goSaveCard, setGoSaveCard] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardPeriod, setCardPeriod] = useState('');
  const [cardCVC, setCardCVC] = useState('');
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const { user, getUser } = useAuth();
  const saveCard = user?.employee.company?.[0]?.save_card;

  const confirm = async () => {
    try {
      setIsLoading(true);
      const companyId = user?.employee.company[0].id;
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/deposit/`,
        {
          card_number: cardNumber.replaceAll(' ', ''),
          card_name: cardName,
          card_period: cardPeriod,
          card_cvc: cardCVC,
        }
      );
      const thismonth = new Date().getMonth() + 1;
      enqueueSnackbar(`${thismonth}월은 미팅 보증금이 무료입니다. (결제 처리가 되지 않습니다.)`)
      setIsLoading(false);
      await getUser();
      paid();
    } catch(err) {
      enqueueSnackbar('처리중 오류가 발생하였습니다. 관리자에 문의해주세요.', { variant: 'error' });
      setIsLoading(false);
      console.error(err);
    }
  };

  const resetAndCloseDialog = () => {
    setGoSaveCard(false);
    setCardNumber('');
    setCardName('');
    setCardPeriod('');
    setCardCVC('');
    setIsError(false);
    setErrorMessage('');
    closeDialog();
  };
  
  const next = () => {
    if (!saveCard) {
      setGoSaveCard(true);
      return;
    } else {
      confirm();
    }
  };

  const validCardNumber = (cardNumber) => {
    if (cardNumber.replaceAll(' ', '').length < 14 || cardNumber.replaceAll(' ', '').length > 16) return false;

    let cardNumberArray = Array.from(cardNumber.replaceAll(' ', ''));

    if (!['3', '4', '5', '6', '9'].includes(cardNumberArray[0])) return false;

    const lastNumber = Number(cardNumberArray.pop());
    cardNumberArray.reverse();
    cardNumberArray = cardNumberArray.map((num, idx) => idx % 2 === 0 ? Number(num) * 2 : Number(num));
    cardNumberArray = cardNumberArray.map((num) => num > 9 ? num - 9 : num);
    let sum = cardNumberArray.reduce((acc, curr) => acc + curr, 0);
    sum += lastNumber;
    const modulo = sum % 10;
    return !modulo;
  };

  const invalidCardPeriod = (cardPeriod) => {
    if (cardPeriod.length !== 5) return true;

    const [month, year] = cardPeriod.split('/');
    if (parseInt(month) === 0 || parseInt(month) > 12) return true;

    const now = new Date();
    const currentYear = now.getFullYear() - 2000;
    const currentMonth = now.getMonth() + 1;

    if (parseInt(year) < currentYear) return true;
    if (parseInt(year) === currentYear && parseInt(month) < currentMonth) return true;
    return false;
  };

  const validCardCVC = (cardCVC) => {
    return cardCVC.length === 3 || cardCVC.length === 4;
  };

  const checkAndConfirm = () => {
    if (!cardNumber || !cardName || !cardPeriod || !cardCVC) {
      setIsError(true);
      setErrorMessage('카드 정보를 모두 입력해주세요.');
      return;
    }
    if (!validCardNumber(cardNumber)) {
      setIsError(true);
      setErrorMessage('카드 번호가 올바르지 않습니다.');
      return;
    }
    if (invalidCardPeriod(cardPeriod)) {
      setIsError(true);
      setErrorMessage('유효기간이 올바르지 않습니다. 월/년 형식으로 입력해주세요. 예) 08/25');
      return;
    }
    if (!validCardCVC(cardCVC)) {
      setIsError(true);
      setErrorMessage('CVC/CVV가 올바르지 않습니다.');
      return;
    }
    setIsError(false);
    confirm();
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs">
      {(saveCard || !goSaveCard) && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
        </Stack>
        <Typography
          variant={smUp ? "h5" : "subtitle1"}
          paragraph
        >
          노쇼 방지를 위한<br/><InfoText>3만원의 미팅 보증금 결제</InfoText>를 요청드립니다.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: "keep-all", mb: 3 }}>
          최근 현직자와 미팅을 잡은 후 미팅에 참석하지 않는 사례가 많아져, <b>3만원의 미팅 보증금(카드 결제)</b>을 도입하였습니다.
        </Typography>

        <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: "keep-all" }}>
          크루와 미팅을 완료하면, 당일 즉시 반환드리는 금액입니다.<br/>번거로우시겠지만 양해 부탁드립니다.
        </Typography>
      </DialogContent>
      )}
      {!saveCard && goSaveCard && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
        </Stack>
        <Typography
          variant={smUp ? "h5" : "subtitle1"}
          paragraph
        >
          카드 정보를 등록해주세요.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: "keep-all", mb: 3 }}>
          최초 1회만 등록해주시면 됩니다.
        </Typography>

        <Stack spacing={2}>
          {isError && (
            <Alert severity="error">{errorMessage}</Alert>
          )}
          <Stack spacing={1}>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
              카드 번호
            </Typography>
            <OutlinedInput
              fullWidth
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              inputComponent={NumberMaskTextInput}
              placeholder="**** **** **** ****"
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
              카드주 이름
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              helperText="카드에 표기된 영문명을 입력해 주세요."
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Stack spacing={1}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
                유효기간
              </Typography>
              <OutlinedInput
                fullWidth
                value={cardPeriod}
                onChange={(e) => setCardPeriod(e.target.value)}
                inputComponent={PeriodMaskTextInput}
                placeholder="MM/YY"
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
                CVC/CVV
              </Typography>
              <OutlinedInput
                fullWidth
                value={cardCVC}
                onChange={(e) => setCardCVC(e.target.value)}
                placeholder="***"
                inputComponent={CVCMaskTextInput}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      )}
      {(saveCard || !goSaveCard) && (
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
          <LoadingButton
            loading={isLoading}
            onClick={closeDialog}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
          >
            취소
          </LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={next}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
          >
            {saveCard ? '보증금 결제에 동의합니다' : '보증금 결제를 위한 카드 등록하기'}
          </LoadingButton>
        </Stack>
      </DialogActions>
      )}
      {!saveCard && goSaveCard && (
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
          <LoadingButton
            loading={isLoading}
            onClick={resetAndCloseDialog}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
          >
            취소
          </LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={checkAndConfirm}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
          >
            카드 등록하고 결제하기
          </LoadingButton>
        </Stack>
      </DialogActions>
      )}
    </Dialog>
  )
};
